import { useEffect, useMemo, useRef, useState } from 'react'

import { useRouter } from 'next/compat/router'

import { useDeactivateSavedFilterMutation } from '@/graphql/generated/hooks'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { parseIncidentsDeepLinkQuery } from '@/redux/incidents/deepLink'
import {
  initializeIncidentsFilters,
  selectHasNonDefaultFilters,
  selectRemoteActiveFilter,
  selectRemoteActiveFilterCriteria,
  selectRemoteActiveFilterLoaded,
  syncActiveFilterFromRemote,
} from '@/redux/incidents/incidentsSlice'

export const useInitializeIncidentsFilters = () => {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const [deactivateSavedFilter] = useDeactivateSavedFilterMutation()
  const hasNonDefaultFilters = useAppSelector(selectHasNonDefaultFilters)
  const remoteActiveFilter = useAppSelector(selectRemoteActiveFilter)
  const remoteActiveFilterCriteria = useAppSelector(
    selectRemoteActiveFilterCriteria
  )
  const isRemoteActiveFilterLoaded = useAppSelector(
    selectRemoteActiveFilterLoaded
  )
  const isRouterReady = router?.isReady
  const routerQuery = router?.query
  const [hasInitializedFilters, setHasInitializedFilters] = useState(false)
  const hasAppliedDeepLinkRef = useRef(false)
  const shouldSkipNextRemoteSyncRef = useRef(false)
  const hasRequestedDeepLinkDeactivateRef = useRef(false)

  const markDeepLinkApplied = () => {
    hasRequestedDeepLinkDeactivateRef.current = false
    hasAppliedDeepLinkRef.current = true
  }

  const deepLinkCriteria = useMemo(() => {
    if (!isRouterReady) return null

    return parseIncidentsDeepLinkQuery(routerQuery ?? {})
  }, [isRouterReady, routerQuery])

  const shouldPreserveSessionFilters = !deepLinkCriteria && hasNonDefaultFilters

  // Initial bootstrap: when router data is ready, set filters from deep-link.
  // If there is no deep-link, initialize from the remote snapshot.
  useEffect(() => {
    if (!isRouterReady || hasInitializedFilters) return
    if (shouldPreserveSessionFilters) {
      shouldSkipNextRemoteSyncRef.current = true
      setHasInitializedFilters(true)
      return
    }
    if (!deepLinkCriteria && !isRemoteActiveFilterLoaded) return

    dispatch(
      initializeIncidentsFilters({
        deepLinkCriteria: deepLinkCriteria ?? null,
      })
    )
    shouldSkipNextRemoteSyncRef.current = true
    hasRequestedDeepLinkDeactivateRef.current = false
    hasAppliedDeepLinkRef.current = !deepLinkCriteria
    setHasInitializedFilters(true)
  }, [
    deepLinkCriteria,
    dispatch,
    hasInitializedFilters,
    isRouterReady,
    isRemoteActiveFilterLoaded,
    shouldPreserveSessionFilters,
  ])

  // Normal sync: after bootstrap, let backend updates refresh local filters.
  // While deep-link handoff is still in progress, hold sync.
  useEffect(() => {
    if (!hasInitializedFilters || !isRemoteActiveFilterLoaded) return
    if (deepLinkCriteria && !hasAppliedDeepLinkRef.current) return

    if (shouldSkipNextRemoteSyncRef.current) {
      shouldSkipNextRemoteSyncRef.current = false
      return
    }

    dispatch(syncActiveFilterFromRemote())
  }, [
    dispatch,
    hasInitializedFilters,
    remoteActiveFilter,
    remoteActiveFilterCriteria,
    isRemoteActiveFilterLoaded,
    deepLinkCriteria,
  ])

  // Deep-link handoff (once): if a remote active filter exists, deactivate it.
  // Then mark deep-link as applied.
  useEffect(() => {
    if (
      !hasInitializedFilters ||
      !deepLinkCriteria ||
      !isRemoteActiveFilterLoaded ||
      hasAppliedDeepLinkRef.current
    )
      return

    if (!remoteActiveFilter?.id) return markDeepLinkApplied()

    if (hasRequestedDeepLinkDeactivateRef.current) return

    hasRequestedDeepLinkDeactivateRef.current = true

    deactivateSavedFilter({
      variables: {
        id: remoteActiveFilter.id,
      },
      refetchQueries: ['SavedIncidentsFilters', 'ActiveIncidentsFilter'],
    }).finally(() => {
      markDeepLinkApplied()
    })
  }, [
    deactivateSavedFilter,
    deepLinkCriteria,
    hasInitializedFilters,
    remoteActiveFilter?.id,
    isRemoteActiveFilterLoaded,
  ])
}
